/** @jsxImportSource @emotion/react */
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { jsx, css } from '@emotion/react'
import { keys, each } from 'lodash'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import JSONPretty from 'react-json-pretty'
import { Modal, Form as BootstrapForm, Col, Row } from 'react-bootstrap'
import { confirmModal } from '../../actions/ui'
import { handleSubmitResult } from '../../actions/form'
import { showSuccess, showError } from '../../actions/Error'
import CardInfoText from '../../components/layout/CardInfoText'
import CommonTable from '../../components/CommonTable'
import { Separator } from '../../components/layout/Separator'
import { WhiteButton } from '../../components/layout/WhiteButton'
import { BlueButton } from '../../components/layout/BlueButton'
import { InlineIcon } from '../../components/layout/InlineIcon'
import { ButtonBar } from '../../components/layout/ButtonBar'
import { FormikInputField } from '../../components/form/InputField'
import { FormikGeneralFormErrors } from '../../components/form/GeneralFormErrors'

/* A "Saved searches" control for a filterable admin list.

   Renders a button that opens a list of saved searches, from which a search can be picked,
   deleted, or the filter currently applied to the page saved under a new name.

   The list of saved searches is itself a normal ItemList (one per page that uses this), so
   anything common to saved searches lives here rather than in each page.

   Props:
     item_list      the ItemList holding the saved searches themselves (required)
     current_filter the filter currently applied to the list being searched
     onActivate     called with a saved search's filters when one is picked - the page applies them
*/

const validation_schema = Yup.object().shape({
    'name': Yup.string().required("Required")
})

const saved_search_headers = {
    name: { name: 'Name' },
    action: { name: '' },
}

// order_by is set by clicking column headers and page is paging bookkeeping - neither is part of
// what an admin means by "the filter", so they are left out of a saved search.
const NON_FILTER_KEYS = ['order_by', 'page']

const isBlank = (value) => {
    return value === null || value === undefined || value === ''
}

export const hasMeaningfulFilter = (filter) => {
    return keys(filter || {}).some((key) => {
        if (NON_FILTER_KEYS.indexOf(key) !== -1) {
            return false
        }
        return ! isBlank(filter[key])
    })
}

const filterWithoutPaging = (filter) => {
    const cleaned = {}
    each(filter || {}, (value, key) => {
        if (NON_FILTER_KEYS.indexOf(key) !== -1 || isBlank(value)) {
            return
        }
        cleaned[key] = value
    })
    return cleaned
}

class SavedSearches extends Component {

    constructor(props) {
        super(props)
        this.state = {show_list_modal: false,
                      show_save_modal: false}
    }

    onShowList = () => {
        const { dispatch, item_list } = this.props
        dispatch(item_list.fetchListIfNeeded())
        this.setState({show_list_modal: true})
    }

    onHideList = () => {
        this.setState({show_list_modal: false})
    }

    onShowSave = () => {
        this.setState({show_save_modal: true})
    }

    onHideSave = () => {
        this.setState({show_save_modal: false})
    }

    onActivate = (item) => {
        const { onActivate } = this.props
        this.onHideList()
        onActivate(item.filters || {})
    }

    onDelete = (item) => {
        const { dispatch, item_list } = this.props

        const on_ok = function(json) {
            if ( json && json.errors ) {
                showError("Delete failed", "The saved search could not be deleted")
                return
            }
            showSuccess("Deleted", "Saved search deleted")
            dispatch(item_list.invalidateList())
            dispatch(item_list.fetchListIfNeeded())
        }

        const onConfirmed = () => {
            return dispatch(item_list.deleteObject(item.id)).then(on_ok)
        }

        return dispatch(confirmModal({
            heading: 'Delete saved search',
            text: `Delete the saved search "${item.name}"? This cannot be undone.`,
            confirm_text: 'Delete',
            onConfirmed: onConfirmed
        }))
    }

    onSave = (values, formik_funcs) => {
        const { dispatch, item_list, current_filter } = this.props
        const that = this

        const on_ok = function() {
            dispatch(item_list.invalidateList())
            dispatch(item_list.fetchListIfNeeded())
            // Both modals close - the list behind is refreshed and ready for next time.
            that.onHideSave()
            that.onHideList()
            showSuccess("Saved", "Search saved")
        }

        return dispatch(item_list.saveNewObject({name: values.name,
                                                 filters: filterWithoutPaging(current_filter)}))
                 .then((res) => handleSubmitResult({res, formik_funcs, on_ok}))
    }

    getCellValue = (header_key, item, index) => {
        switch( header_key ) {
            case 'name':
                return (
                    <div onClick={ () => this.onActivate(item) } css={ saved_search_cell_style }>
                      <a css={ saved_search_name_style }>{item.name}</a>
                    </div>
                )
            case 'action':
                return (
                    <ButtonBar>
                      <InlineIcon icon_name="delete" onClick={ () => this.onDelete(item) } />
                    </ButtonBar>
                )
            default:
                return undefined
        }
    }

    renderSavedSearchesModal = () => {
        const that = this
        const { saved_searches, saved_searches_loading, current_filter } = this.props
        const can_save_search = hasMeaningfulFilter(current_filter)

        return (
            <Modal show={true}
                   onHide={that.onHideList}
                   size="lg" >
              <Modal.Header closeButton>
                <Modal.Title>
                  Saved searches
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
                <CardInfoText>
                  Click a saved search to apply it to the list.
                </CardInfoText>
                <CommonTable is_loading={ saved_searches_loading }
                             empty_message="There are no saved searches."
                             headers={ saved_search_headers }
                             items={ saved_searches }
                             item_list={that.props.item_list}
                             getCellValue={ that.getCellValue }
                />

                {/* Only offered when there is actually a filter to save. */}
                { can_save_search &&
                  <div>
                    <Separator variant="h30" />
                    <BlueButton onClick={ that.onShowSave } auto_disable={false}>
                      Save current search
                    </BlueButton>
                  </div>
                }
              </Modal.Body>
            </Modal>
        )
    }

    renderSaveSearchModal = () => {
        const that = this
        const { current_filter } = this.props
        const filters_to_save = filterWithoutPaging(current_filter)

        return (
            <Modal show={true}
                   onHide={that.onHideSave} >
              <Modal.Header closeButton>
                <Modal.Title>
                  Save current search
                </Modal.Title>
              </Modal.Header>

              <Formik initialValues={{name: ''}}
                      onSubmit={ that.onSave }
                      validationSchema={ validation_schema }>
                {formik_props => {
                    return (
                        <Form>
                          <Modal.Body>
                            <CardInfoText>
                              The current filter is saved under this name, so you can apply it
                              again later.
                            </CardInfoText>

                            {/* Shown as raw json on purpose - it is what actually gets stored, which
                                makes it obvious what the saved search will reapply. */}
                            <JSONPretty data={filters_to_save} />

                            <BootstrapForm.Group>
                              <BootstrapForm.Label>Search name</BootstrapForm.Label>
                              <FormikInputField name="name" placeholder="Search name" />
                            </BootstrapForm.Group>

                            <FormikGeneralFormErrors render={(msg) => <Row><Col>{msg}</Col></Row>} />
                          </Modal.Body>

                          <Modal.Footer>
                            <BlueButton type="submit" auto_disable={false}>Save search</BlueButton>
                          </Modal.Footer>
                        </Form>
                    )
                }}
              </Formik>
            </Modal>
        )
    }

    render() {
        const that = this
        const { show_list_modal, show_save_modal } = this.state

        return (
            <div>
              <WhiteButton onClick={ that.onShowList } auto_disable={false}>
                Saved searches
                <span css={ chevron_spacing }>
                  <InlineIcon icon_name="chevron-down" />
                </span>
              </WhiteButton>

              { show_list_modal && that.renderSavedSearchesModal() }
              { show_save_modal && that.renderSaveSearchModal() }
            </div>
        )
    }

}

function mapStateToProps(state, props) {
    const { item_list } = props
    return {
        saved_searches: item_list.getVisibleObjects() || [],
        saved_searches_loading: item_list.isLoading(),
    }
}

export default connect(mapStateToProps)(SavedSearches)

const chevron_spacing = css`
margin-left: 5px;
`

// The whole cell is the target, not just the name text, so the click area is generous.
const saved_search_cell_style = css`
cursor: pointer;
display: block;
width: 100%;
min-height: 24px;
`

const saved_search_name_style = css`
text-decoration: underline;
`
